import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useState } from 'react';
import { Button, EyeIcon, RHFTextField } from '@higo/ui';
import { FormattedMessage } from 'react-intl';
import { StyleProps } from '@summer/jst-react';
import { setYupDefaultMessages } from '../../config/yupLocale';
import styled from 'styled-components';

setYupDefaultMessages();

const schema = object({
  login: string().required(),
  password: string().required(),
});

export interface LoginFormModel {
  login: string;
  password: string;
}

const defaultValues: LoginFormModel = {
  login: '',
  password: '',
};

export type LoginFormProps = {
  isLoading: boolean;
  onSubmit: (value: LoginFormModel) => Promise<unknown>;
} & StyleProps;

const PasswordFieldWrapper = styled.div`
  position: relative;

  input {
    padding-right: 2.75rem;
  }
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  top: 3.3rem;
  right: 0.75rem;
  transform: translateY(-50%);
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.primary[500]};
`;

export const LoginForm: FC<LoginFormProps> = ({
  className,
  isLoading,
  onSubmit,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<LoginFormModel>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <RHFTextField
        control={control}
        label="common.email"
        name="login"
        autoComplete="email"
        id="email-login-form"
      />
      <PasswordFieldWrapper>
        <RHFTextField
          control={control}
          type={showPassword ? 'text' : 'password'}
          label="common.password"
          name="password"
          autoComplete="current-password"
          id="password-login-form"
        />
        <PasswordToggleButton
          type="button"
          onClick={() => setShowPassword((x) => !x)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          id="toggle-password-visibility"
        >
          <EyeIcon crossed={!showPassword} size={20} />
        </PasswordToggleButton>
      </PasswordFieldWrapper>
      <Button
        type="submit"
        disabled={isLoading || isSubmitting}
        isLoading={isLoading || isSubmitting}
        fullWidth
        id="login-button"
      >
        <FormattedMessage id="common.login" />
      </Button>
    </form>
  );
};
